import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { publicApi } from '../lib/api';
import { getSessionId } from '../lib/userParams';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { ShieldCheck, Mail, CheckCircle2, Loader2, ArrowLeft } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const REASONS = [
  'Too many emails',
  'Not relevant anymore',
  "I didn't sign up",
  'Not interested in the offers',
  'Other',
];

export default function Unsubscribe() {
  const location = useLocation();
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // Set custom tab title (bypasses PageTracker default)
    document.title = 'Unsubscribe – The Loan Alliance';
    // Prefill email from ?email= param
    const usp = new URLSearchParams(location.search);
    const em = usp.get('email');
    if (em) setEmail(em);
  }, [location.search]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email.trim()) { setError('Please enter your email address.'); return; }
    if (!EMAIL_RE.test(email.trim())) { setError('Please enter a valid email address.'); return; }
    setSubmitting(true);
    try {
      const usp = new URLSearchParams(location.search);
      const source = usp.get('source') || usp.get('utm_source') || '';
      const campaign = usp.get('campaign') || usp.get('utm_campaign') || '';
      await publicApi.submitForm('unsubscribe', {
        email: email.trim().toLowerCase(),
        reason: reason || '',
        note: note.trim(),
        source,
        campaign,
        page: '/unsubscribe',
        user_agent: navigator.userAgent || '',
        sessionId: getSessionId(),
      });
      setDone(true);
    } catch (err) {
      setError('Something went wrong. Please try again in a moment.');
      toast({ title: 'Unsubscribe failed', description: 'Please retry shortly.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50/40 flex items-center justify-center p-5">
      <div className="w-full max-w-lg">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-4">
          <ArrowLeft className="w-4 h-4" /> Back to site
        </Link>

        <div className="rounded-2xl bg-white border border-slate-200 shadow-xl overflow-hidden">
          <div className="bg-gradient-to-br from-emerald-500 to-teal-600 px-6 py-6 text-white flex items-center gap-3">
            <div className="w-11 h-11 rounded-lg bg-white/20 border border-white/30 flex items-center justify-center backdrop-blur">
              <ShieldCheck className="w-6 h-6" />
            </div>
            <div>
              <div className="font-serif-display text-xl font-bold">The Loan Alliance</div>
              <div className="text-xs text-emerald-50">Email Preferences</div>
            </div>
          </div>

          <div className="p-6 sm:p-8">
            {done ? (
              <div className="text-center py-4">
                <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
                  <CheckCircle2 className="w-8 h-8 text-emerald-600" />
                </div>
                <h2 className="font-serif-display mt-4 text-2xl font-bold text-slate-900">You're unsubscribed</h2>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                  We've received your request. <span className="font-semibold text-slate-800">{email}</span> will no longer receive marketing emails from The Loan Alliance. It may take up to 48 hours for the change to fully take effect.
                </p>
                <p className="mt-4 text-xs text-slate-500">
                  Changed your mind? You can always re-subscribe from our homepage anytime.
                </p>
                <Link to="/" className="inline-block mt-6">
                  <Button className="bg-emerald-600 hover:bg-emerald-700 text-white h-11 px-6">Return to Homepage</Button>
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <h2 className="font-serif-display text-2xl font-bold text-slate-900">Unsubscribe from emails</h2>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                    Sorry to see you go. Enter your email address below and we'll immediately remove you from our marketing list.
                  </p>
                </div>

                <div>
                  <Label className="text-xs">Email address</Label>
                  <div className="relative mt-1">
                    <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => { setEmail(e.target.value); setError(''); }}
                      className="pl-9 h-11"
                      placeholder="you@example.com"
                      autoComplete="email"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-xs">Reason (optional)</Label>
                  <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {REASONS.map((r) => (
                      <button
                        type="button"
                        key={r}
                        onClick={() => setReason(reason === r ? '' : r)}
                        className={`text-left text-sm px-3 py-2 rounded-md border transition-colors ${
                          reason === r
                            ? 'bg-emerald-600 text-white border-emerald-600'
                            : 'bg-white text-slate-700 border-slate-200 hover:border-emerald-300'
                        }`}
                      >
                        {r}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label className="text-xs">Tell us more (optional)</Label>
                  <Textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    className="mt-1 min-h-[80px]"
                    placeholder="Anything you'd like to share to help us improve…"
                    maxLength={500}
                  />
                </div>

                {error && <div className="text-sm text-red-600">{error}</div>}

                <Button type="submit" disabled={submitting} className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 text-white">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Unsubscribe me'}
                </Button>

                <p className="text-[11px] text-slate-500 leading-relaxed">
                  Note: transactional emails (e.g. security notices, account confirmations) may still be sent as required. Our privacy policy is available <Link to="/privacy-policy" className="text-emerald-700 underline">here</Link>.
                </p>
              </form>
            )}
          </div>
        </div>

        <p className="mt-5 text-center text-xs text-slate-400">
          © {new Date().getFullYear()} The Loan Alliance. All rights reserved.
        </p>
      </div>
    </div>
  );
}
